import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Dimensions, Image, Platform, StyleSheet, Text, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { SafeAreaView } from 'react-native-safe-area-context'

import { DS } from '../../../../design-system'
import { useStore } from '../../../../models'
import { events } from '../../../../models/analytics/analytics-events'
import { TableTourStep } from '../../../../models/game/walkthrough/table-tour'
import { fontFamily } from '../../../../theme'
import { numberWithCommas } from '../../../../utils/numberWithCommas'
import { ChipsLayout } from '../../Board/ChipsLayout'
import { PlayerCell } from '../../PlayerCell'
import { PlayerChips } from '../../PlayerChips'
import { PlayerRole } from '../../PlayerRole'
import ClubSvg from '../assets/Club.svg'
import DiamondSvg from '../assets/Diamond.svg'
import HeartSvg from '../assets/Heart.svg'
import SpadeSvg from '../assets/Spade.svg'
import SuitCards from '../assets/suit-cards.svg'
import SuitsBackdropSvg from '../assets/SuitsBackdrop.svg'
import TableSvg from '../assets/Table.svg'
import { PointingLine } from '../PointingLine'
import { WalkthroughOverlayActions } from '../WalkthroughOverlayActions'
import ChipsPinkMaroon from '../../assets/ChipsPinkMaroon.svg'

export const TableTour = observer(() => {
  const {
    game: { currentStep, currentPosition, players, mainPot, myPlayer, step, isLastStep },
    analytics: { setEvent },
    session: { user, selectedDailyTask, updateDailyTaskStatus }
  } = useStore()

  const titleRef = useRef<Text>(null)
  const [chipsContainerWidth, setChipsContainerWidth] = useState(0)
  const [potContainerWidth, setPotContainerWidth] = useState(0)
  const insets = useSafeAreaInsets()

  const markTableTourTaskAsComplete = async () => {
    try {
      if (selectedDailyTask) {
        await updateDailyTaskStatus()
      }
    } catch (error) {
      console.log('markTableTourTaskAsComplete error', error)
    }
  }

  useEffect(() => {
    if (step === 1) {
      setEvent(events.homescreen.table_tour_started, {
        userId: user?._id.toString()
      })
    }
    if (step === 11) {
      //Step 11 = 'pot' will confirm the end of table tour
      markTableTourTaskAsComplete()
    }
  }, [step, setEvent, user?._id])

  if (!currentStep) return null
  const tableTourStep = currentStep as TableTourStep

  const player = players.find((p) => p.role === tableTourStep.id)
  const isRight = player?.role === 'BB'

  const chips = tableTourStep.id === 'chips' && myPlayer
  const pot = tableTourStep.id === 'pot'
  const isNumberOfSuits = tableTourStep.id === 'no_of_suits'
  const isDisplayingSuitCards = tableTourStep.id === 'cards_in_suit'
  const isLayout = tableTourStep.id === 'layout'
  const PLAYER_HAND_HORIZONTAL_PADDING = 16

  const elementPosition = useMemo(() => {
    if (!currentPosition) return {}

    const left = 10
    const right = 10

    const top = currentPosition.y - insets.top

    return {
      top,
      ...(isRight || chips ? { right } : { left })
    }
  }, [step, currentPosition, insets.top, chips, isRight])

  if (step === 0) return <TableTourFirstScreen />

  return (
    <SafeAreaView style={styles.safeAreaView}>
      <View style={styles.content}>
        <PointingLine>
          {tableTourStep.id && ['D', 'SB', 'BB', 'hole', 'pot', 'chips'].includes(tableTourStep.id) ? (
            <View style={[styles.textContainer, isLastStep ? { marginTop: 40 } : {}]}>
              {['D', 'SB', 'BB'].includes(tableTourStep.id) && (
                <PlayerRole role={tableTourStep.id as PlayerRole} size="big" />
              )}
              {tableTourStep.id === 'pot' && (
                <View style={{ marginBottom: 46 }}>
                  <ChipsLayout />
                  <Text style={styles.chipsText}>{mainPot ? numberWithCommas(mainPot) : 0}</Text>
                </View>
              )}
              <DS.Text variant="Heading 1/Regular" color={'Global/White'}>
                {tableTourStep.title}
              </DS.Text>
              <DS.Text variant="Body Medium/Regular" color="Global/White">
                {tableTourStep.description}
              </DS.Text>
            </View>
          ) : (
            <View style={[styles.tableTourFirstThreeStepsContainer, isLastStep ? { marginTop: 40 } : {}]}>
              <DS.Text variant="Heading 1/Regular" color={'Global/White'}>
                {tableTourStep.title}
              </DS.Text>
              {isLayout ? (
                <DS.Frame alignItems="center" justifyContent="center" style={{ marginTop: 20 }}>
                  <ChipsPinkMaroon width={64} height={64} />
                </DS.Frame>
              ) : null}
              {isDisplayingSuitCards ? (
                <DS.Frame alignItems="center" justifyContent="center" style={{ marginTop: 20 }}>
                  {/* <CardGroupSvg /> */}
                  <Image
                    source={require('../assets/CardGroup.png')}
                    resizeMode="contain"
                    style={{ width: Dimensions.get('screen').width, height: 200 }}
                  />
                </DS.Frame>
              ) : null}
              {isNumberOfSuits ? (
                <DS.Frame gap="12" justifyContent="center" alignItems="center" style={{ marginTop: 10 }}>
                  <DS.Frame row gap="8">
                    <DS.Text variant="Heading 3/Regular" color={'Global/White'}>
                      Hearts
                    </DS.Text>
                    <HeartSvg />
                  </DS.Frame>
                  <DS.Frame row gap="8">
                    <DS.Text variant="Heading 3/Regular" color={'Global/White'}>
                      Diamonds
                    </DS.Text>
                    <DiamondSvg />
                  </DS.Frame>
                  <DS.Frame row gap="8">
                    <DS.Text variant="Heading 3/Regular" color={'Global/White'}>
                      Clubs
                    </DS.Text>
                    <ClubSvg />
                  </DS.Frame>
                  <DS.Frame row gap="8">
                    <DS.Text variant="Heading 3/Regular" color={'Global/White'}>
                      Spades
                    </DS.Text>
                    <SpadeSvg />
                  </DS.Frame>
                  <View style={{ marginTop: 20 }}>
                    <Image
                      source={require('../assets/SuitCards.png')}
                      resizeMode="contain"
                      style={{ width: 200, height: 200 }}
                    />
                    {/* <SuitCards /> */}
                  </View>
                </DS.Frame>
              ) : null}
            </View>
          )}
        </PointingLine>
      </View>
      {step > 0 && <WalkthroughOverlayActions />}
      {player ? (
        <View style={{ position: 'absolute', ...elementPosition }}>
          <PlayerCell player={player} right={isRight} savePositionReference={false} />
        </View>
      ) : null}
      {chips ? (
        <View
          style={{ position: 'absolute', ...elementPosition }}
          onLayout={({
            nativeEvent: {
              layout: { width }
            }
          }) => setChipsContainerWidth(width)}
        >
          <PlayerChips chips={myPlayer.chips} color="Global/White" right chipsSize={18} />
        </View>
      ) : null}
      {/* {pot ? (
        <View
          style={[
            styles.chipsContainer,
            { position: 'absolute', top: (currentPosition?.y ?? 0) + (currentPosition?.height ?? 0) / 2 }
          ]}
          onLayout={({
            nativeEvent: {
              layout: { width }
            }
          }) => setPotContainerWidth(width)}
        >
          <ChipsLayout />
          <Text style={styles.chipsText}>{mainPot ? numberWithCommas(mainPot) : 0}</Text>
        </View>
      ) : null} */}
    </SafeAreaView>
  )
})

const TableTourFirstScreen = () => {
  return (
    <SafeAreaView style={styles.safeAreaView}>
      <View style={styles.content}>
        <View style={styles.backdropContainer}>
          <SuitsBackdropSvg />
        </View>
        <View style={styles.titleContainer}>
          <View>
            <TableSvg />
          </View>
          <Text style={[styles.text, styles.title]}>Game Set Up</Text>
          {/* <Text style={[styles.text, styles.subtitle]}>Test your instincts with hole cards.</Text> */}
          <Text style={[styles.text, styles.body]}>
            Whether your're new to Texas Hold'em or just want a refresher, this guide will go over the basics — from the
            cards to the positions around the table.
          </Text>
        </View>
      </View>
      <WalkthroughOverlayActions />
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  safeAreaView: {
    width: '100%',
    height: '100%',
    flex: 1,
    paddingTop: 16
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 20
  },
  backdropContainer: {
    position: 'absolute',
    top: '5%',
    transform: [{ scale: 1.1 }]
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 10,
    padding: 40
  },
  textContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 10
  },
  text: {
    color: 'white',
    textAlign: 'center'
  },
  title: {
    fontFamily: fontFamily.FeatureDeckRegular,
    fontSize: 32
  },
  subtitle: {
    fontFamily: fontFamily.GTAmericaStandardMedium,
    fontSize: 18
  },
  body: {
    fontFamily: fontFamily.GTAmericaStandardRegular,
    fontSize: 16
  },
  caption: {
    fontFamily: fontFamily.GTAmericaStandardRegular,
    fontSize: 16,
    textAlign: 'center'
  },
  chipsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 10,
    width: '100%'
  },
  chipsText: {
    color: 'white',
    fontWeight: '500',
    fontFamily: fontFamily.FeatureDeckBold,
    fontSize: 20
  },
  tableTourFirstThreeStepsContainer: {
    display: 'flex',
    flex: 0.7,
    justifyContent: 'flex-start',
    alignItems: 'center'
  }
})
